import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  Chip,
  FormControlLabel,
  Checkbox,
  Divider,
  Tooltip,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import type { ActivityType, UpdateActivityTypeRequest, ActivityTypeRequiredFields } from '../../../types/activityType';
import { getOperationTypeLabel, getOperationTypeColor } from '../../../utils/operationTypeUtils';

interface EditActivityTypeProps {
  open: boolean;
  activityType: ActivityType | null;
  onClose: () => void;
  onSubmit: (activityTypeId: number, activityTypeData: UpdateActivityTypeRequest) => Promise<void>;
}

export default function EditActivityType({
  open,
  activityType,
  onClose,
  onSubmit,
}: EditActivityTypeProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [name, setName] = useState('');
  const [requiredFields, setRequiredFields] = useState<ActivityTypeRequiredFields>({
    clientRequired: false,
    providerRequired: false,
    stockRequired: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (activityType) {
      setName(activityType.name);
      setRequiredFields(activityType.requiredFields);
    }
  }, [activityType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!activityType) return;
    
    if (!name.trim()) {
      setError('Назва типу активності обов\'язкова');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit(activityType.id, {
        name: name.trim(),
        requiredFields,
      });
      
      onClose();
    } catch (err) {
      console.error('Error updating activity type:', err);
      setError('Не вдалося оновити тип активності. Спробуйте ще раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  if (!activityType) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '100vw',
          overflowX: 'hidden',
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Редагувати тип активності</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, overflowX: 'hidden' }}>
            <TextField
              autoFocus
              label="Назва типу активності"
              fullWidth
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              error={!!error}
              helperText={error}
              disabled={loading}
              required
            />

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Тип операції (не можна змінити):
              </Typography>
              <Chip 
                label={getOperationTypeLabel(activityType.operationType)}
                color={getOperationTypeColor(activityType.operationType)}
                size="medium"
              />
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Обов'язкові поля для цього типу активності
              </Typography>
              <Tooltip 
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Налаштування обов'язкових полів
                    </Typography>
                    <Typography variant="body2">
                      Оберіть, які поля будуть обов'язковими для цього типу активності.
                      Деякі поля можуть бути заблоковані залежно від типу операції.
                    </Typography>
                  </Box>
                }
                arrow
                placement="right"
              >
                <IconButton size="small" sx={{ p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={requiredFields.clientRequired}
                    onChange={(e) => setRequiredFields({
                      ...requiredFields,
                      clientRequired: e.target.checked
                    })}
                    disabled={loading}
                  />
                }
                label="Клієнт обов'язковий"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={requiredFields.providerRequired}
                    onChange={(e) => setRequiredFields({
                      ...requiredFields,
                      providerRequired: e.target.checked
                    })}
                    disabled={loading}
                  />
                }
                label="Постачальник обов'язковий"
              />
              {activityType.operationType !== 'BETWEEN_STOCKS' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={requiredFields.stockRequired}
                      onChange={(e) => setRequiredFields({
                        ...requiredFields,
                        stockRequired: e.target.checked
                      })}
                      disabled={loading}
                    />
                  }
                  label="Склад обов'язковий"
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Скасувати
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Збереження...' : 'Зберегти'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
